import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import { log } from 'src/services/app/log.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { authService } from 'src/services/api/auth.service'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'

/**
 * Update-account mutation for user profile fields (firstName, lastName).
 *
 * Persists via `authService.updateAccount` (PATCH /me), full-replaces the
 * ACCOUNT_DATA query cache, then broadcasts for cross-tab sync.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function useUpdateAccountMutation() {
  const queryClient = useQueryClient()

  const updateAccountMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_ACCOUNT],
    mutationFn: (payload) => authService.updateAccount(payload),

    onSuccess: (updatedAccount) => {
      queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], updatedAccount)
      broadcastAccountData(updatedAccount)
      notifyService.success(notifyMsgs.success.updateAccount)
    },

    onError: (error) => {
      log.error('[Account] updateAccount failed', error)
      notifyService.error(notifyMsgs.error.updateAccount)
    },
  })

  return {
    updateAccount: wrapMutationAsync(updateAccountMutation.mutateAsync),
    isUpdatingAccount: updateAccountMutation.isPending,
    updateAccountError: updateAccountMutation.error,
  }
}
